/**
 * @file CommoraBrandPanel.tsx
 * @description Панель бренда Commora — левая половина входа и служебных
 * страниц на большом экране (phase19.29, макет login-mockup.png).
 *
 * Логотип с подписью, заголовок «Весь ваш торговый бизнес — в одной
 * платформе», пояснение и сетка из восьми модулей (реестр `MODULES`: новый
 * модуль = запись). Плитки не кликабельны и не реагируют на наведение.
 * Фон — светлый с мягкими сине-фиолетовыми формами (CSS/SVG, без растровых
 * картинок) и `aria-hidden`; место справа внизу оставлено декоративным под
 * будущие настоящие снимки кабинета. Заголовок и список модулей доступны
 * программам чтения с экрана.
 */

import {
  Boxes,
  Factory,
  Share2,
  ShoppingCart,
  Store,
  Truck,
  Users,
  Warehouse,
  type LucideIcon,
} from "lucide-react";

import { CommoraLogo } from "./CommoraLogo";

interface ModuleItem {
  icon: LucideIcon;
  /** Подпись в две строки, как на макете. */
  lines: [string, string];
}

/** Модули платформы (решение пользователя: показываем сразу, «скоро» не пишем). */
const MODULES: ModuleItem[] = [
  { icon: Store, lines: ["Сайт", "и витрина"] },
  { icon: Boxes, lines: ["Товары", "и каталог"] },
  { icon: Warehouse, lines: ["Склад", "и запасы"] },
  { icon: Users, lines: ["Клиенты", "и CRM"] },
  { icon: ShoppingCart, lines: ["Продажи", "и заказы"] },
  { icon: Truck, lines: ["Доставка", "и логистика"] },
  { icon: Share2, lines: ["Каналы", "продаж"] },
  { icon: Factory, lines: ["Производство", "и планирование"] },
];

export function CommoraBrandPanel() {
  return (
    <section
      aria-labelledby="commora-brand-title"
      className="commora-brand-panel relative flex min-h-screen flex-col gap-8 overflow-hidden px-8 py-10 xl:px-16 xl:py-12"
    >
      {/* Декоративный фон: мягкая диагональная волна снизу справа. */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[45%] w-full"
        viewBox="0 0 800 400"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="commora-wave" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" style={{ stopColor: "var(--brand-purple)", stopOpacity: 0.55 }} />
            <stop offset="1" style={{ stopColor: "var(--brand-blue)", stopOpacity: 0.35 }} />
          </linearGradient>
        </defs>
        <path d="M0 400 L0 330 C 220 300 420 210 560 120 C 650 60 730 20 800 0 L800 400 Z" fill="url(#commora-wave)" />
        <path d="M0 400 L0 370 C 260 350 480 290 640 200 C 710 160 760 120 800 90 L800 400 Z" style={{ fill: "var(--surface-lowest)", fillOpacity: 0.35 }} />
      </svg>

      <div className="relative flex flex-col gap-8">
        <CommoraLogo size="lg" caption="Commerce Operating Platform" />
        <div className="flex max-w-xl flex-col gap-3">
          <h2 id="commora-brand-title" className="commora-hero-title text-on-surface">
            Весь ваш торговый бизнес — в одной платформе
          </h2>
          <p className="text-sm text-[var(--on-surface-strong)]">
            Создавайте интернет-магазин, управляйте товарами, складом, клиентами, продажами,
            доставкой, каналами продаж и производством.
          </p>
        </div>
        <ul className="grid max-w-2xl grid-cols-4 gap-2 xl:gap-3" aria-label="Модули платформы">
          {MODULES.map(({ icon: Icon, lines }) => (
            <li
              key={lines.join(" ")}
              className="flex min-w-0 flex-col gap-2 rounded-xl bg-card/85 p-2 ring-1 ring-outline-variant xl:p-3"
            >
              <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                <Icon className="size-5" aria-hidden />
              </span>
              <span className="commora-module-label break-words text-on-surface">
                {lines[0]}
                <br />
                {lines[1]}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
